import mapboxgl from 'mapbox-gl'

/**
 * Camera-independent 2D frame for markup geometry TRANSFORMS (rotate/resize/
 * build). Screen space is only Euclidean while the camera is unchanged — under
 * pitch it's a perspective frame, so doing math there and unprojecting bakes
 * shear into the saved lat/lng (rectangle → parallelogram, circle → ellipse).
 * The Web-Mercator plane is flat and conformal: rotations are rigid and
 * perpendicularity survives, regardless of the camera. Pointer input still
 * goes through map.unproject (screen → geo) and display through map.project;
 * only the math in between lives here.
 *
 * Axes match screen axes at the default camera (x east, y south = down), so
 * plane angles equal screen angles at bearing 0 / pitch 0.
 */
export interface IPlanePoint {
  x: number
  y: number
}

// Scale Mercator's 0..1 world square to comfortable magnitudes (≈ px at z23).
const PLANE_SCALE = 2 ** 23
export function toPlane([lng, lat]: number[]): IPlanePoint {
  const m = mapboxgl.MercatorCoordinate.fromLngLat({ lng, lat })
  return { x: m.x * PLANE_SCALE, y: m.y * PLANE_SCALE }
}

export function fromPlane(p: IPlanePoint): number[] {
  const lngLat = new mapboxgl.MercatorCoordinate(
    p.x / PLANE_SCALE,
    p.y / PLANE_SCALE,
    0,
  ).toLngLat()
  return [lngLat.lng, lngLat.lat]
}

export function rotatePlane(
  p: IPlanePoint,
  c: IPlanePoint,
  rad: number,
): IPlanePoint {
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return {
    x: c.x + (p.x - c.x) * cos - (p.y - c.y) * sin,
    y: c.y + (p.x - c.x) * sin + (p.y - c.y) * cos,
  }
}

export function planeCentroid(pts: IPlanePoint[]): IPlanePoint {
  return {
    x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
    y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
  }
}

/**
 * A rectangle centred on `center`, its edges turned to the screen axes for `bearingDeg`, as geo
 * corners [TL, TR, BR, BL] in that frame.
 *
 * Sized in plane units, so the caller owns the conversion from whatever it measured — and that is
 * the point. Sizing a drag from its two endpoints' ground span inflates the axis pointing away
 * from a pitched camera, because ground distance per pixel grows with depth: a square sweep at
 * pitch 60 laid down a footprint up to 2.4x longer than it was wide, and levelling the pitch
 * revealed it (STX1-1062). A caller that measures in pixels and converts once, at one scale,
 * cannot.
 *
 * The result foreshortens under pitch, as any rectangle lying on the ground does; what it is at
 * every camera is a real plane rectangle with perpendicular axes, which is what the resize and
 * rotate math relies on.
 */
export function planeRectangle(
  center: IPlanePoint,
  halfWidth: number,
  halfHeight: number,
  bearingDeg: number,
): number[][] {
  const theta = (bearingDeg * Math.PI) / 180
  const corner = (dx: number, dy: number): number[] =>
    fromPlane(
      rotatePlane({ x: center.x + dx, y: center.y + dy }, center, theta),
    )

  return [
    corner(-halfWidth, -halfHeight),
    corner(halfWidth, -halfHeight),
    corner(halfWidth, halfHeight),
    corner(-halfWidth, halfHeight),
  ]
}
